import React, {useState} from "react";
import Button from "@amzn/meridian/button";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Icon from "@amzn/meridian/icon";
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import { isBlankString } from "../../../utils/string-utils";
import { getYesterdayDate } from "../models";
import { createShiftPlan, getClockWindowNameBasedOnSelectedOption, getPlanTypeBasedOnSelectedTab } from "../../../services/shiftPlanService";
import { PlanVersion } from "../../../types/shiftPlan";
import { useAppDispatch } from "../../../store";
import { showNotification } from "../../../store/slices/errorSlice";
import { ErrorCode } from "../../../utils/constants";

interface NoExistingShiftPlanProps {
    selectedTab: string;
    selectedDate: string;
    selectedShift: string;
    /** Called when the API signals a plan already exists — parent opens the modal */
    onNeedsTakeOver: (displayMessage: string) => void;
    /** Called on successful plan creation — parent handles dispatch + navigate */
    onPlanSuccess: (planId: string) => void;
}

const NoExistingShiftPlan: React.FC<NoExistingShiftPlanProps> = ({
    selectedTab,
    selectedDate,
    selectedShift,
    onNeedsTakeOver,
    onPlanSuccess,
}) => {
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    const isFormValid = selectedShift.trim() !== "";

    const onClick = async () => {
        // Validate that both date and shift are selected
        if (isBlankString(selectedDate) || isBlankString(selectedShift)) {
            dispatch(showNotification({
                title: "Missing Selection",
                message: "Please select both date and shift before generating the shift plan.",
                type: "warning"
            })
            );
            return;
        }
        const userSelectedDate = new Date(selectedDate);
        const yesterdayDate = new Date(getYesterdayDate());
        if (userSelectedDate < yesterdayDate) {
            dispatch(showNotification({
                title: "Invalid Date",
                message: "Cannot generate plan for past date. Please try generating for another date.",
                type: "warning"
            })
            );
            return;
        }

        setIsGenerating(true);

        // Call the API to generate shift plan
        const response = await createShiftPlan(selectedDate, getClockWindowNameBasedOnSelectedOption(selectedShift), getPlanTypeBasedOnSelectedTab(selectedTab), PlanVersion.STATIC_V1);

        if (response.errorDetails?.errorCode === ErrorCode.PLAN_OWNED_BY_ANOTHER_USER) {
            onNeedsTakeOver(response.errorDetails.errorMessage); // trigger modal
        } else if (response.status && response.status !== 'FAILED' && response.planId) {
            // Check if the operation was successful
            onPlanSuccess(response.planId);
        }
        // Error handling is now done inside createShiftPlan via ErrorModal
        setIsGenerating(false);
    }

    return (
        <Row spacing="400" alignmentVertical="center" widths={["fill", "fit"]}>
            <Row spacing="300" alignmentVertical="center">
                <Icon tokens={infoKnockoutTokens} color="secondary" />
                <Text color="secondary">
                    {selectedTab === "sos"
                        ? "Start of Shift (SOS) "
                        : "Prior Day (PDP) "}
                    plan has not been generated.
                </Text>
            </Row>

            <Button onClick={onClick} disabled={!isFormValid || isGenerating}>
                {isGenerating ? "Generating..." : "Start shift plan"}
            </Button>
        </Row>
    );
}

export default NoExistingShiftPlan;
